import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, AlertCircle } from 'lucide-react'
import api from '../services/api'
import { validateUsername as validateUsernameFormat } from '../utils/inputValidation'
import { trackOnboardingComplete } from '../services/adTracking'

export default function ProfileSetup() {
  const [username, setUsername] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null)
  const [usernameError, setUsernameError] = useState('')
  const [usernameChecking, setUsernameChecking] = useState(false)
  const usernameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigate = useNavigate()

  const validateAge = (dob: string): boolean => {
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age >= 15
  }

  const checkUsername = (un: string) => {
    setUsernameAvailable(null)
    setUsernameError('')
    if (un.length === 0) return

    const formatCheck = validateUsernameFormat(un)
    if (!formatCheck.isValid) {
      setUsernameError(formatCheck.error || 'Invalid username')
      setUsernameAvailable(false)
      return
    }

    if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current)
    setUsernameChecking(true)
    usernameTimerRef.current = setTimeout(async () => {
      try {
        const result = await api.checkUsernameAvailability(un)
        if (!result.valid) {
          setUsernameError(result.message || 'Invalid username')
          setUsernameAvailable(false)
        } else if (!result.available) {
          setUsernameError('Username is already taken')
          setUsernameAvailable(false)
        } else {
          setUsernameError('')
          setUsernameAvailable(true)
        }
      } catch {
        setUsernameError('Unable to verify username')
        setUsernameAvailable(false)
      } finally {
        setUsernameChecking(false)
      }
    }, 300)
  }

  const handleSubmit = async () => {
    setError('')

    if (!username) {
      setError('Username is required')
      return
    }
    if (usernameAvailable !== true) {
      setError(usernameError || 'Please enter a valid username')
      return
    }
    if (!dateOfBirth) {
      setError('Date of birth is required')
      return
    }
    if (!validateAge(dateOfBirth)) {
      setError('You must be at least 15 years old to use OnRecord')
      return
    }

    setLoading(true)

    try {
      // Use username as displayName if no name from OAuth
      const userProfile = localStorage.getItem('user_profile')
      const currentUser = userProfile ? JSON.parse(userProfile) : null
      const displayName = currentUser?.name || currentUser?.displayName || username

      const result = await api.completeProfile({
        displayName,
        username,
      })

      if (result.success) {
        trackOnboardingComplete(username)
        const updatedUser = result.user || { username, displayName, profileCompleted: true }
        localStorage.setItem('user_profile', JSON.stringify(updatedUser))
        navigate('/')
      } else {
        setError(result.message || 'Failed to create profile')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create profile'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Pick a username
          </h1>
          <p className="text-gray-500">
            You can add your name, photo, and bio later in Settings
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <div className="relative">
              <span className="absolute left-4 top-3.5 text-gray-400 font-medium">@</span>
              <input
                type="text"
                value={username}
                onChange={(e) => {
                  const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
                  setUsername(val)
                  checkUsername(val)
                }}
                className={`w-full pl-9 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-onrecord-orange focus:border-transparent ${
                  usernameError ? 'border-red-400' : usernameAvailable === true ? 'border-green-400' : 'border-gray-300'
                }`}
                placeholder="Choose a username"
                maxLength={20}
                autoFocus
              />
              {username.length >= 1 && (
                <div className="absolute right-3 top-3.5">
                  {usernameChecking && (
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-onrecord-orange rounded-full animate-spin" />
                  )}
                  {!usernameChecking && usernameAvailable === true && (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                  {!usernameChecking && usernameAvailable === false && (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              )}
            </div>
            {usernameError ? (
              <p className="text-xs text-red-500 mt-1">{usernameError}</p>
            ) : usernameAvailable === true ? (
              <p className="text-xs text-green-500 mt-1">Username is available</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                3-20 characters. Lowercase letters, numbers, and underscores only.
              </p>
            )}
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date of Birth
            </label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              max={new Date(Date.now() - 15 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-onrecord-orange focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              You must be at least 15 years old to use OnRecord
            </p>
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 bg-onrecord-orange hover:bg-onrecord-orange-dark text-white rounded-lg font-semibold text-lg transition-colors disabled:opacity-50"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating...
              </div>
            ) : (
              'Get Started'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
